import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    no: '01',
    title: '需求訪談',
    desc: '深入了解品牌定位、空間需求、預算與工期目標',
    icon: 'ri-discuss-line',
  },
  {
    no: '02',
    title: '現場丈量',
    desc: '現地評估空間條件、結構限制與動線可行性',
    icon: 'ri-ruler-2-line',
  },
  {
    no: '03',
    title: '概念提案',
    desc: '平面配置草案、設計概念、材料方向初步呈現',
    icon: 'ri-draft-line',
  },
  {
    no: '04',
    title: '設計深化',
    desc: '完整施工圖、材料確認、預算細化與調整討論',
    icon: 'ri-pencil-ruler-2-line',
  },
  {
    no: '05',
    title: '工程施作',
    desc: '進度管理、現場品質監控、定期工程回報',
    icon: 'ri-tools-line',
  },
  {
    no: '06',
    title: '完工交付',
    desc: '驗收確認、使用說明、後續維護建議',
    icon: 'ri-checkbox-circle-line',
  },
];

function useStepReveal(total: number) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          // Stagger: reveal one card every 120ms
          let count = 0;
          const interval = setInterval(() => {
            count += 1;
            setVisibleCount(count);
            if (count >= total) clearInterval(interval);
          }, 120);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [total]);

  return { sectionRef, visibleCount };
}

export default function ProcessPreview() {
  const { sectionRef, visibleCount } = useStepReveal(steps.length);

  return (
    <section className="py-24 md:py-32 bg-[#f4f2ef]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-4">Our Process</p>
          <h2
            className="font-bold text-[#1a1a1a] leading-[1.35] mb-4"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
          >
            透明清楚的合作流程
          </h2>
          <p className="text-[#5a5a5a] max-w-lg mx-auto" style={{ fontSize: '1.1875rem' }}>
            從第一次諮詢到完工交付，每個環節清楚分明，
            讓業主全程安心、不迷路。
          </p>
        </div>

        {/* Steps grid — animated */}
        <div
          ref={sectionRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12"
        >
          {steps.map((s, i) => {
            const visible = i < visibleCount;
            return (
              <div
                key={i}
                className="bg-[#fafaf8] rounded-2xl p-8 relative overflow-hidden"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(28px)',
                  transition: 'opacity 0.55s ease, transform 0.55s ease',
                }}
              >
                {/* Big number watermark */}
                <span
                  className="absolute top-4 right-5 font-light text-[#e2ddd8] select-none pointer-events-none"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '4rem', lineHeight: 1 }}
                >
                  {s.no}
                </span>

                <div className="relative z-10">
                  {/* Step icon + number badge */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#b8956a] rounded-full flex-shrink-0">
                      <i className={`${s.icon} text-white text-base`}></i>
                    </div>
                    {/* Connector line */}
                    {i % 3 !== 2 && (
                      <div
                        className="hidden lg:block flex-1 h-px bg-[#e2ddd8]"
                        style={{
                          opacity: visible ? 1 : 0,
                          transition: `opacity 0.4s ease ${0.2 + i * 0.12}s`,
                        }}
                      ></div>
                    )}
                  </div>

                  {/* Step label */}
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-[#b8956a] text-xs font-semibold tracking-widest">STEP {s.no}</span>
                  </div>

                  <h3
                    className="font-semibold text-[#1a1a1a] mb-3 leading-[1.45]"
                    style={{ fontSize: '1.125rem' }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-[#5a5a5a] text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {steps.map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-300"
              style={{
                width: i < visibleCount ? '24px' : '8px',
                height: '6px',
                backgroundColor: i < visibleCount ? '#b8956a' : '#e2ddd8',
                transition: 'all 0.3s ease',
              }}
            ></div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/process"
            className="inline-flex items-center gap-2 text-[#b8956a] font-medium hover:gap-3 transition-all cursor-pointer text-[1rem]"
          >
            了解完整規劃流程
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
